import React, { useMemo } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Heading,
  Badge,
  Tooltip,
  Icon,
  useColorModeValue,
  Skeleton,
  Divider,
  HStack,
  Select,
} from "@chakra-ui/react";
import { MdInfo, MdTrendingUp, MdTrendingDown } from "react-icons/md";

interface ConversionTableProps {
  dashboardData: any;
  isLoading?: boolean;
}

const ConversionTable: React.FC<ConversionTableProps> = ({
  dashboardData,
  isLoading = false,
}) => {
  const [entityType, setEntityType] = React.useState<
    "seller" | "corban" | "promoter"
  >("seller");

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const highPerformanceColor = useColorModeValue("green.600", "green.400");
  const lowPerformanceColor = useColorModeValue("red.600", "red.400");

  const subTextColor = useColorModeValue("gray.600", "gray.400");

  const conversionData = useMemo(() => {
    if (
      !dashboardData ||
      !dashboardData.quantityBalanceGraphic ||
      !dashboardData.quantityProposalPayedGraphic
    ) {
      return { topPerformers: [], lowPerformers: [] };
    }

    const extractCorbanId = (sellerId: string): string | null => {
      if (!sellerId || !sellerId.includes(".")) return null;
      return sellerId.split(".")[1];
    };

    // Create data structure for each entity type
    const sellersData: Array<{
      id: string | number;
      name: string;
      consultations: number;
      sales: number;
      conversionRate: number;
    }> = [];

    const corbansData: Array<{
      id: string | number;
      name: string;
      consultations: number;
      sales: number;
      conversionRate: number;
    }> = [];

    const promotersData: Array<{
      id: string | number;
      name: string;
      consultations: number;
      sales: number;
      conversionRate: number;
    }> = [];

    // Maps to collect data by ID for each entity type
    const sellersMap = new Map<
      string,
      { id: string; name: string; consultations: number; sales: number }
    >();
    const corbansMap = new Map<
      string,
      { id: string; name: string; consultations: number; sales: number }
    >();
    const promotersMap = new Map<
      string,
      { id: string; name: string; consultations: number; sales: number }
    >();

    // Process quantityBalanceGraphic to get consultations per entity
    if (
      dashboardData.quantityBalanceGraphic &&
      dashboardData.quantityBalanceGraphic.length > 0
    ) {
      dashboardData.quantityBalanceGraphic.forEach((item: any) => {
        // Process sellers
        if (item.sellerId && item.sellerFullName) {
          const id = item.sellerId.toString();
          const existing = sellersMap.get(id) || {
            id,
            name: item.sellerFullName,
            consultations: 0,
            sales: 0,
          };
          existing.consultations += item.total || 0;
          sellersMap.set(id, existing);
        }

        // Process corbans
        if (item.corbanName) {
          const corbanId = (
            item.corbanId || extractCorbanId(item.sellerId)
          )?.toString();
          if (corbanId) {
            const existing = corbansMap.get(corbanId) || {
              id: corbanId,
              name: item.corbanName,
              consultations: 0,
              sales: 0,
            };
            existing.consultations += item.total || 0;
            corbansMap.set(corbanId, existing);
          }
        }

        // Process promoters
        if (item.promoterName && item.promoterName !== "(campo em branco)") {
          const promoterId = (
            item.promoterId ||
            `name-${item.promoterName.replace(/\s+/g, "-").toLowerCase()}`
          ).toString();
          const existing = promotersMap.get(promoterId) || {
            id: promoterId,
            name: item.promoterName,
            consultations: 0,
            sales: 0,
          };
          existing.consultations += item.total || 0;
          promotersMap.set(promoterId, existing);
        }
      });
    }

    // Group and aggregate sales data by entity type
    if (
      dashboardData.quantityProposalPayedGraphic &&
      dashboardData.quantityProposalPayedGraphic.length > 0
    ) {
      // First group and sum the sales data
      const salesBySellerId = new Map<string, number>();
      const salesByCorbanId = new Map<string, number>();
      const salesByPromoterId = new Map<string, number>();

      dashboardData.quantityProposalPayedGraphic.forEach((item: any) => {
        // Process seller sales
        if (item.sellerId) {
          const id = item.sellerId.toString();
          const currentTotal = salesBySellerId.get(id) || 0;

          salesBySellerId.set(id, currentTotal + (item.total || 0));
        }

        // Process corban sales
        if (item.corbanId) {
          const id = item.corbanId.toString();
          const currentTotal = salesByCorbanId.get(id) || 0;
          salesByCorbanId.set(id, currentTotal + (item.total || 0));
        }

        // Process promoter sales (using corbanId relationships)
        if (item.corbanId) {
          // Find all promoters associated with this corban
          const corbanId = item.corbanId.toString();

          // Get all promoters from this corban from the quantityBalanceGraphic
          const relatedPromoters = dashboardData.quantityBalanceGraphic.filter(
            (entry: any) => {
              const entryCorbanId = (
                entry.corbanId || extractCorbanId(entry.sellerId)
              )?.toString();
              return (
                entryCorbanId === corbanId &&
                entry.promoterName &&
                entry.promoterName !== "(campo em branco)"
              );
            }
          );

          // Distribute sales proportionally among promoters
          if (relatedPromoters.length > 0) {
            const sharePerPromoter =
              (item.total || 0) / relatedPromoters.length;

            relatedPromoters.forEach((promoter: any) => {
              const promoterId = (
                promoter.promoterId ||
                `name-${promoter.promoterName.replace(/\s+/g, "-").toLowerCase()}`
              ).toString();

              const currentTotal = salesByPromoterId.get(promoterId) || 0;
              salesByPromoterId.set(
                promoterId,
                currentTotal + sharePerPromoter
              );
            });
          }
        }
      });

      // Now update our entity maps with the sales data
      salesBySellerId.forEach((salesTotal, id) => {
        const seller = sellersMap.get(id);
        if (seller) {
          seller.sales = salesTotal;
        }
      });

      salesByCorbanId.forEach((salesTotal, id) => {
        const corban = corbansMap.get(id);
        if (corban) {
          corban.sales = salesTotal;
        }
      });

      salesByPromoterId.forEach((salesTotal, id) => {
        const promoter = promotersMap.get(id);
        if (promoter) {
          promoter.sales = salesTotal;
        }
      });
    }

    // Convert maps to arrays and calculate conversion rates
    sellersMap.forEach((seller) => {
      sellersData.push({
        ...seller,
        conversionRate:
          seller.consultations > 0
            ? (seller.sales / seller.consultations) * 100
            : 0,
      });
    });

    corbansMap.forEach((corban) => {
      corbansData.push({
        ...corban,
        conversionRate:
          corban.consultations > 0
            ? (corban.sales / corban.consultations) * 100
            : 0,
      });
    });

    promotersMap.forEach((promoter) => {
      promotersData.push({
        ...promoter,
        conversionRate:
          promoter.consultations > 0
            ? (promoter.sales / promoter.consultations) * 100
            : 0,
      });
    });

    // Sort each array by conversion rate
    sellersData.sort((a, b) => b.conversionRate - a.conversionRate);
    corbansData.sort((a, b) => b.conversionRate - a.conversionRate);
    promotersData.sort((a, b) => b.conversionRate - a.conversionRate);

    // Select the appropriate data based on the current entity type
    let entityData;
    switch (entityType) {
      case "seller":
        entityData = sellersData;
        break;
      case "corban":
        entityData = corbansData;
        break;
      case "promoter":
        entityData = promotersData;
        break;
      default:
        entityData = sellersData;
    }

    // Get top and bottom performers
    const topPerformers = entityData.slice(0, 5).map((item) => ({
      name: item.name,
      consultations: item.consultations,
      sales: item.sales,
      conversionRate: item.conversionRate,
      type: entityType,
    }));

    const lowPerformers = [...entityData]
      .sort((a, b) => a.conversionRate - b.conversionRate)
      .slice(0, 5)
      .map((item) => ({
        name: item.name,
        consultations: item.consultations,
        sales: item.sales,
        conversionRate: item.conversionRate,
        type: entityType,
      }));

    return { topPerformers, lowPerformers };
  }, [dashboardData, entityType]);

  const handleEntityTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEntityType(e.target.value as "seller" | "corban" | "promoter");
  };

  const formatPercentage = (value: number) => {
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      style: "percent",
      useGrouping: true,
    });
  };

  if (isLoading) {
    return (
      <Box
        borderRadius="xl"
        bg={cardBg}
        p={6}
        boxShadow="md"
        border="1px solid"
        borderColor={borderColor}
      >
        <Skeleton height="300px" />
      </Box>
    );
  }

  return (
    <Box
      borderRadius="xl"
      bg={cardBg}
      p={6}
      boxShadow="md"
      border="1px solid"
      borderColor={borderColor}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex alignItems="center">
          <Heading size="md">Análise de Conversão</Heading>
          <Tooltip label="Análise da taxa de conversão (Pagas ÷ Consultas) para identificar os melhores e piores desempenhos" placement="bottom">
            <Icon as={MdInfo} ml={2} color="gray.500" />
          </Tooltip>
        </Flex>
        <HStack spacing={2}>
          <Text fontSize="sm" color={subTextColor}>
            Visualizar:
          </Text>
          <Select
            size="sm"
            value={entityType}
            onChange={handleEntityTypeChange}
            width="auto"
          >
            <option value="seller">Vendedores</option>
            <option value="corban">Corbans</option>
            <option value="promoter">Promotoras</option>
          </Select>
        </HStack>
      </Flex>

      <Box overflowX="auto">
        <Heading
          size="sm"
          mb={2}
          color={highPerformanceColor}
          display="flex"
          alignItems="center"
        >
          <Icon as={MdTrendingUp} mr={1} /> Top 5 Conversores
        </Heading>
        <Table variant="simple" size="sm" mb={6}>
          <Thead bg={headerBg}>
            <Tr>
              <Th>Nome</Th>
              <Th isNumeric>Consultas</Th>
              <Th isNumeric>Pagas</Th>
              <Th isNumeric>Taxa de Conversão</Th>
            </Tr>
          </Thead>
          <Tbody>
            {conversionData.topPerformers.length > 0 ? (
              conversionData.topPerformers.map((item, index) => (
                <Tr key={`top-${index}`}>
                  <Td fontWeight="medium">{item.name}</Td>
                  <Td isNumeric>
                    {item.consultations.toLocaleString("pt-BR")}
                  </Td>
                  <Td isNumeric>{item.sales.toLocaleString("pt-BR")}</Td>
                  <Td isNumeric>
                    <Badge colorScheme="green" px={2} py={1}>
                      {formatPercentage(item.conversionRate / 100)}
                    </Badge>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={4} textAlign="center" py={4}>
                  <Text color={subTextColor}>Nenhum dado disponível</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        <Divider my={4} />

        <Heading
          size="sm"
          mb={2}
          color={lowPerformanceColor}
          display="flex"
          alignItems="center"
        >
          <Icon as={MdTrendingDown} mr={1} /> 5 Conversores com Menor Desempenho
        </Heading>
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th>Nome</Th>
              <Th isNumeric>Consultas</Th>
              <Th isNumeric>Pagas</Th>
              <Th isNumeric>Taxa de Conversão</Th>
            </Tr>
          </Thead>
          <Tbody>
            {conversionData.lowPerformers.length > 0 ? (
              conversionData.lowPerformers.map((item, index) => (
                <Tr key={`low-${index}`}>
                  <Td fontWeight="medium">{item.name}</Td>
                  <Td isNumeric>
                    {item.consultations.toLocaleString("pt-BR")}
                  </Td>
                  <Td isNumeric>{item.sales.toLocaleString("pt-BR")}</Td>
                  <Td isNumeric>
                    <Badge colorScheme="red" px={2} py={1}>
                      {formatPercentage(item.conversionRate / 100)}
                    </Badge>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={4} textAlign="center" py={4}>
                  <Text color={subTextColor}>Nenhum dado disponível</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Text fontSize="xs" color={subTextColor} mt={4} textAlign="right">
        * Taxa de Conversão = (Pagas ÷ Consultas) × 100%
      </Text>
    </Box>
  );
};

export default ConversionTable;
